import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Button from './Button';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Panel that slides over the right side of the page; the page stays visible behind it.
 * Esc, the close button and a click on the dimmed page all call onClose.
 */
export default function SidePanel({ open, onClose, title, description, children, footer }) {
  const panelRef = useRef(null);
  const bodyRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const panel = panelRef.current;
    (bodyRef.current.querySelector(FOCUSABLE) || panel).focus();

    const onKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...panel.querySelectorAll(FOCUSABLE)];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previous && document.contains(previous)) previous.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[3200]">
      <div className="absolute inset-0 scrim" onMouseDown={() => onCloseRef.current()} aria-hidden="true" />
      <section
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="side-panel-title"
        tabIndex={-1}
        className="panel-in absolute inset-y-0 right-0 w-panel max-w-full flex flex-col bg-sheet border-l border-rule shadow-overlay outline-none"
      >
        <header className="shrink-0 flex items-start gap-3 px-5 pt-4 pb-3 border-b border-rule">
          <div className="flex-1 min-w-0">
            <h2 id="side-panel-title" className="text-section text-ink">{title}</h2>
            {description && <p className="mt-0.5 text-small text-graphite">{description}</p>}
          </div>
          <Button variant="ghost" size="sm" square onClick={() => onCloseRef.current()} aria-label="Close panel" title="Close (Esc)">
            <X size={16} strokeWidth={1.75} aria-hidden="true" />
          </Button>
        </header>
        <div ref={bodyRef} className="flex-1 min-h-0 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <footer className="shrink-0 flex justify-end gap-2 px-5 py-3 border-t border-rule">{footer}</footer>}
      </section>
    </div>,
    document.body,
  );
}
